import { Prices } from '../prices/types';

export type SubscriptionType =
	| 'freetrial'
	| 'monthly'
	| 'quarterly'
	| 'lifetime'
	| 'family'
	| 'subscribe'
	| 'one-time-purchase'
	| 'already-trialing'
	| 'already-subscribed'
	| 'previously-trialed';

const planSkuIds = ['monthly', 'quarterly', 'yearly', 'lifetime', 'family'] as const;
export type PlanCadence = (typeof planSkuIds)[number];
export const isValidPlan = (value: string): value is PlanCadence => planSkuIds.includes(value as PlanCadence);

const oneTimePurchasePlanSkuIds = ['sevenMin'] as const;
export type OneTimePurchasePlanCadence = (typeof oneTimePurchasePlanSkuIds)[number];
export const isValidOneTimePurchasePlan = (value: string): value is OneTimePurchasePlanCadence =>
	oneTimePurchasePlanSkuIds.includes(value as OneTimePurchasePlanCadence);

// Recurring subscription cadences — the only plans Checkout Sessions
// (created with `mode: 'subscription'`) can create. `lifetime` and every
// `OneTimePurchasePlanCadence` are single up-front charges, so they're excluded.
// An inclusion list fails safe to the legacy flow for any unknown/future plan.
const recurringPlanSkuIds = [
	'monthly',
	'quarterly',
	'yearly',
	'family',
] as const satisfies readonly PlanCadence[];
export type RecurringPlanCadence = (typeof recurringPlanSkuIds)[number];
const recurringPlans: ReadonlySet<string> = new Set(recurringPlanSkuIds);

export const isRecurringPlan = (
	plan: PlanCadence | OneTimePurchasePlanCadence | null | undefined,
): plan is RecurringPlanCadence => plan !== null && plan !== undefined && recurringPlans.has(plan);

export const getPrice = (
	plan: PlanCadence | OneTimePurchasePlanCadence | null,
	prices: Prices['current'] | Prices['original'],
): number => {
	if (!plan) return 0;

	if (isValidOneTimePurchasePlan(plan)) {
		return prices.oneTimePurchases?.[plan] ?? 0;
	}

	if (isValidPlan(plan)) {
		return prices[plan] ?? 0;
	}

	return 0;
};

export interface PurchaseType {
	type: SubscriptionType;
	modifier?: string;
	duration?: number;
	units?: string | null;
	price?: number;
	plan?: PlanCadence | OneTimePurchasePlanCadence;
	isFreeTrial?: boolean;
	promotion?: string;
}

export type GiftPrice = null | {
	originalPrice: number;
	price: number;
	currency?: string;
};

export interface PurchaseParams {
	plan: PlanCadence | OneTimePurchasePlanCadence;
	coupon?: string | null;
	promotion?: string | null;
	samsungGUID?: string | null;
	guestPass?: string | null;
	calmjwt?: string | null;
	is30DayOverride?: boolean;
	purchaseType?: PurchaseType | null;
	giftPrice?: GiftPrice;
	isUpdateBillingScreen?: boolean;
	trialLengthOverride?: number | null;
	isPartner?: boolean;

	// Calm Sleep App
	sku?: string;
	skus?: { yearly?: string; monthly?: string };
	offerCode?: string;
	partnershipId?: string;
}

export type RouteSpecificPurchaseParams = Partial<PurchaseParams> & {
	freeTrialLength?: number | null;
	hasFreeTrial?: boolean | null;
	freeTrialLengthUnit?: string | null;
};

export const SET_PURCHASE_PARAMS = 'SET_PURCHASE_PARAMS';
